<template>
  <div class="isr-details">
    <Modal
      size="medium"
      :primary-button-label="
        currentSelectedTab === 'isr_details' ? 'Update' : ''
      "
      tertiary-button-label="Cancel"
      close-on-click-outside
      active
      class="isr-details__modal"
      :primary-button-disabled="submitDisabled"
      @onClose="onClose()"
      @onConfirm="onSubmitClick()">
      <div slot="header">ISR number: {{ isrDetails.case_number }}</div>
      <div slot="content">
        <div class="isr-details__tab-area">
          <Tabs :items="isrTabs" @onClick="onTabActivated" />
        </div>
        <div class="isr-details__tab-content">
          <div v-if="currentSelectedTab === 'isr_details'">
            <notification
              v-if="errorMessage"
              icon="info-circle"
              type="error"
              title="Review the errors on this page"
              classes="custom-toast">
              <div v-for="err in errorMessage" :key="err">
                <div v-html="err"></div>
              </div>
            </notification>
            <notification
              icon="info-circle"
              type="info"
              classes="custom-toast"
              class="create-isr__modal__content__info">
              <div>
                Requestor dept field depends on CM Visibility. Please check your
                profile and choose accordingly.
              </div>
            </notification>
            <Form
              ref="isrDetailsForm"
              :form-data="formData"
              :default-values="fieldValues"
              @onSubmitForm="onSubmit"
              @updateOnSearch="updateOnSearch"
              @onChange="onFormChange"></Form>
          </div>
          <attachment-list
            v-else-if="currentSelectedTab === 'isr_attachments'"
            :attachments="isrAttachments"
            has-download
            :has-delete="false"
            class="isr-details__tab-content__attachments"
            v-on="$listeners" />
          <email-activities
            v-else-if="currentSelectedTab === 'isr_emails'"
            :email-activities="isrEmailActivities"
            :case-data="isrDetails"
            v-on="$listeners" />
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
// Components
import EmailActivities from "@/components/EmailActivities/EmailActivities.vue";

// constants
import { isrDetailsForm, isrTabs } from "@/config/isrConstant.js";

export default {
  name: "ISRDetails",
  components: {
    EmailActivities,
  },
  props: {
    caseDetailsSfdc: {
      type: Object,
      required: true,
    },
    isrDetails: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      formData:
        isrDetailsForm(
          this.isrDetails?.countryList,
          this.isrDetails?.cityList,
          this.isrDetails?.owner_id,
          this.isrDetails?.sub_type
        ) || [],
      fieldValues: {},
      isrTabs,
      currentSelectedTab: "isr_details",
      isrAttachments: [],
      isrEmailActivities: [],
      uniqueCountryList: this.isrDetails?.countryList,
      uniqueCityList: this.isrDetails?.cityList,
      submitDisabled: true,
      errorMessage: null,
    };
  },
  created() {
    const formFields = this.formData.map((form) => form.formFields);
    //replaceAll replaces charCode 160 with 32(space).
    const isrDetailsCopy = JSON.parse(
      JSON.stringify(this.isrDetails).replaceAll(" ", " ")
    );
    [].concat.apply([], formFields).forEach((el) => {
      this.fieldValues[el.name] =
        typeof isrDetailsCopy[el.name] !== "undefined"
          ? isrDetailsCopy[el.name]
          : null;
    });
    this.fieldValues["contact_phone"] = this.isrDetails["contact_phone"] || "-";
    if (this.isrDetails["last_modified_by_name"] === "Maersk API Management") {
      this.fieldValues["last_modified_by_name"] =
        this.isrDetails["created_by_name"];
    }
  },
  methods: {
    onClose() {
      this.$emit("onModalClose");
    },
    onSubmitClick() {
      this.$refs.isrDetailsForm.validate();
    },
    onSubmit(formState, changedFields) {
      const data = {
        ...changedFields,
        country: changedFields?.country
          ? changedFields?.country?.id
          : undefined,
        city: changedFields?.city ? changedFields?.city?.id : undefined,
      };
      this.$emit(
        "updateIsr",
        this.isrDetails.id,
        data,
        () => {
          this.$emit("onModalClose");
          this.$emit("refreshIsrData");
        },
        (err) => {
          this.getErrorMessages(err);
          document.getElementsByClassName("modal__content")[0].scrollTop = 0;
        }
      );
    },
    onTabActivated({ selectedItemId, updatedTabData }) {
      const tabDetails = {
        isr_attachments: () => {
          this.$emit(
            "fetchAttachmentsFromSfdc",
            this.isrAttachments.length,
            this.isrDetails?.id,
            (data) => {
              this.isrAttachments = (data || []).map((item) => ({
                fileName: item?.name,
                type: item?.file_extension || item?.name.split(".").pop(),
                id: item?.id,
                createdBy: item?.created_by_name,
                lastModifiedDate: item?.last_modified_date,
              }));
            },
            true
          );
          return;
        },
        isr_emails: () => {
          this.$emit(
            "fetchEmailActivities",
            this.isrEmailActivities.length,
            this.isrDetails?.id,
            (data) => {
              this.isrEmailActivities = data;
            },
            true
          );
          return;
        },
        default: () => {
          return;
        },
      };
      this.currentSelectedTab = selectedItemId;
      this.isrTabs = updatedTabData;
      (tabDetails[`${selectedItemId}`] || tabDetails["default"])();
    },
    onFormChange(currentDiff, diffWithDefaultValues) {
      if (
        Object.prototype.hasOwnProperty.call(currentDiff || {}, "resolver_dept")
      ) {
        this.$refs.isrDetailsForm?.updateFormState("collaboration_reason", "");
        this.$refs.isrDetailsForm?.updateFormState(
          "collaboration_reason_other",
          ""
        );
      }
      if (Object.keys(diffWithDefaultValues).length) {
        this.submitDisabled = false;
      } else {
        this.submitDisabled = true;
      }
    },
    updateOnSearch(searchTerm, field) {
      if (field.name === "country") {
        this.$emit(
          "getCountryList",
          `countryName=${searchTerm.searchValue}`,
          (countryList) => {
            this.uniqueCountryList = [
              ...this.isrDetails?.countryList,
              ...Array.from(
                new Set(countryList.map((item) => item.countryGeoId))
              ).map((countryGeoId) => {
                const countryData = countryList.find(
                  (item) => item.countryGeoId === countryGeoId
                );
                if (countryData.countryName === this.isrDetails?.country_name) {
                  return;
                }
                return {
                  id: countryGeoId,
                  name: countryData.countryName,
                  label: countryData.countryName,
                  value: countryData.countryName,
                };
              }),
            ];
            this.formData = isrDetailsForm(
              this.uniqueCountryList,
              this.uniqueCityList
            );
          }
        );
      } else {
        this.$emit(
          "getCountryList",
          `cityName=${searchTerm.searchValue}`,
          (cityList) => {
            this.uniqueCityList = [
              ...this.isrDetails?.cityList,
              ...Array.from(
                new Set(cityList.map((item) => item.maerskGeoLocationId))
              ).map((maerskGeoLocationId) => {
                const cityData = cityList.find(
                  (item) => item.maerskGeoLocationId === maerskGeoLocationId
                );
                if (cityData.cityName === this.isrDetails?.city_name) {
                  return;
                }
                return {
                  id: maerskGeoLocationId,
                  name: cityData.cityName,
                  label: cityData.cityName,
                  value: cityData.cityName,
                };
              }),
            ];
            this.formData = isrDetailsForm(
              this.uniqueCountryList,
              this.uniqueCityList
            );
          }
        );
      }
    },
    getErrorMessages(err) {
      const errorMessagesList = [];
      err.forEach((error) => {
        const errObj = JSON.parse(error);
        errObj.forEach((item) => {
          if (item && "message" in item && item.message) {
            errorMessagesList.push(item.message);
          }
        });
      });
      this.errorMessage = errorMessagesList;
    },
  },
};
</script>

<style lang="scss">
.isr-details {
  .modal--medium {
    height: 92vh;
    position: fixed;
    bottom: 0;
    width: 100%;
  }
  &__tab-area {
    position: sticky;
    top: 0;
    background-color: #fff;
    z-index: 2;
  }
  &__tab-content__attachments {
    margin-top: 1rem;
  }
  .form-input__field:disabled {
    color: var(--Gray-900);
  }
}
</style>
